/**
 * A MarketingImage component is an image with dynamic text drawn over it.
 * Used to create profile promotion assets for a pro to share on social media.
 * Example would be a "Book me on StyleSeat" image with their vanity URL on it.
 * */

import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import { colors, toRGB } from '@styleseat/brand';
import { wrapText, segmentLines } from './utils';

const MarketingImage = ({
  assetID,
  image,
  width,
  height,
  renderedCallback,
  content,
  isFontFaceLoaded,
}) => {
  const canvasRef = useRef(null);

  const getPixelRatio = context => {
    const dpr = window.devicePixelRatio || 1;
    const bsr = context.webkitBackingStorePixelRatio
      || context.mozBackingStorePixelRatio
      || context.msBackingStorePixelRatio
      || context.oBackingStorePixelRatio
      || context.backingStorePixelRatio || 1;
    return dpr / bsr;
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext?.('2d');
    if (ctx) {
      // Scale drawing to device DPI (HiDPI)
      const ratio = getPixelRatio(ctx);
      canvas.width = width * ratio;
      canvas.height = height * ratio;
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);

      const background = new Image();
      // Set CORS to allow a local data URL from a background image hosted on a CDN
      background.setAttribute('crossorigin', 'anonymous');
      background.width = width;
      background.height = height;
      background.onload = () => {
        ctx.drawImage(background, 0, 0, width, height);

        content.forEach(chunk => {
          const {
            text,
            textColor = toRGB(colors.spark),
            fontSize = 12,
            fontWeight = 400,
            lineHeight = 15,
            highlightColor = toRGB(colors.punch),
            highlightBlocks = [],
            offsetBottom,
            maxWidth = null,
          } = chunk;

          const maxTextWidthChars = maxWidth || Math.floor(width / 7) - 10;
          const lines = wrapText(text, maxTextWidthChars);
          const segmentedLines = segmentLines(lines, text, highlightBlocks, highlightColor);
          // move text up a bit to make room for multiple lines
          const textY = height - offsetBottom - (Math.ceil(fontSize / 3) * (lines.length - 1));
          // Set font properties used within the canvas, done late to allow the font to load
          ctx.font = `normal ${fontWeight} ${fontSize}px/${lineHeight}px SpaceGrotesk`;
          ctx.fillStyle = textColor;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'bottom';
          ctx.save();
          // null indicates non-highlighted, use default set from textColor property
          let currentFontColor = null;
          segmentedLines.forEach((segments, i) => {
            const y = textY + (i * (lineHeight + 2));
            // start x at the offset from image edge where the entire line would start if centered
            let x = (width / 2) - ctx.measureText(lines[i]).width / 2;

            segments.forEach(([fontColor, snippet]) => {
              if (fontColor !== currentFontColor) {
                if (fontColor === null) {
                  ctx.restore();
                  currentFontColor = null;
                } else {
                  ctx.fillStyle = fontColor;
                  currentFontColor = fontColor;
                }
              }
              // fill text from center of snippet block, then set current x to end of snippet
              const halfSnippetWidth = ctx.measureText(snippet).width / 2;
              x += halfSnippetWidth;
              ctx.fillText(snippet, x, y);
              x += halfSnippetWidth;
            });
          });
          ctx.restore();
        });

        renderedCallback(canvas.toDataURL());
      };
      background.src = image;
    }
  }, [canvasRef, isFontFaceLoaded, image]);

  return (
    <canvas ref={canvasRef} data-testid={`marketing_image_canvas_${assetID}`} style={{ width, height }} />
  );
};

const contentObjectType = PropTypes.shape({
  /* Text to draw over the image */
  text: PropTypes.string.isRequired,
  /* Color of the text, in rgb(R, G, B) format. */
  textColor: PropTypes.string,
  /* Pixels from the bottom of the image to start drawing the text */
  offsetBottom: PropTypes.number.isRequired,
  /* Size of font drawn over image */
  fontSize: PropTypes.number,
  /* Weight of font drawn over image */
  fontWeight: PropTypes.number,
  /* Line height of font drawn over image */
  lineHeight: PropTypes.number,
  /* Color to highlight text matched from highlightBlocks */
  highlightColor: PropTypes.string,
  /* Parts of the text to colorize with highlightColor,
  each an object with start and end indexes */
  highlightBlocks: PropTypes.arrayOf(
    PropTypes.shape({
      start: PropTypes.number.isRequired,
      end: PropTypes.number.isRequired,
    }),
  ),
  /* Max width in characters to wrap text, default guesses from image width */
  maxWidth: PropTypes.number,
});

MarketingImage.propTypes = {
  assetID: PropTypes.string.isRequired,
  /* Path/URL to the image to draw over, normally from a require() */
  image: PropTypes.string.isRequired,
  /* Image width */
  width: PropTypes.number.isRequired,
  /* Image height */
  height: PropTypes.number.isRequired,
  /* Text content to overlay the image */
  content: PropTypes.arrayOf(contentObjectType),
  /* Once rendering is complete this is called with rendered image data URL as an argument */
  renderedCallback: PropTypes.func,
  /* FontFace object to wait on the loading of */
  isFontFaceLoaded: PropTypes.bool,
};

MarketingImage.defaultProps = {
  renderedCallback: () => { },
  content: [],
  isFontFaceLoaded: false,
};

export default MarketingImage;
